// React
import React, { useEffect } from "react";

// Third party imports
import { useDispatch, useSelector } from "react-redux";

// Icons
import { IoCheckmark } from "react-icons/io5";

// API
import { getColors } from "api/misc.jsx";

// Redux
import { setColors } from "redux/slices/miscSlice.jsx";
import { setError } from "redux/slices/snackbarSlice.jsx";

// Default color options - fallback if API fails
const DEFAULT_COLOR_OPTIONS = [
  { color: "#FFFFFF", bgcolor: "#000000", name: "Black" },
  { color: "#000000", bgcolor: "#FFFFFF", name: "White" },
  { color: "#FFFFFF", bgcolor: "#EF4444", name: "Red" },
  { color: "#FFFFFF", bgcolor: "#F97316", name: "Orange" },
  { color: "#FFFFFF", bgcolor: "#EAB308", name: "Yellow" },
  { color: "#FFFFFF", bgcolor: "#22C55E", name: "Green" },
  { color: "#FFFFFF", bgcolor: "#3B82F6", name: "Blue" },
  { color: "#FFFFFF", bgcolor: "#8B5CF6", name: "Purple" },
  { color: "#FFFFFF", bgcolor: "#EC4899", name: "Pink" },
  { color: "#000000", bgcolor: "#F3F4F6", name: "Gray" },
];

/**
 * ColorPicker component - displays circular color choices from API
 * @param {Object} props - Component props
 * @param {Object} props.chosenColor - Current chosen color object with color and bgcolor
 * @param {Function} props.handleColorChange - Function to call when color changes
 * @param {boolean} props.disabled - Whether the picker is disabled
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} ColorPicker component
 */
const ColorPicker = ({
  chosenColor = { color: "#FFFFFF", bgcolor: "#000000" },
  handleColorChange,
  disabled = false,
  className = "",
}) => {
  const dispatch = useDispatch();
  const colors = useSelector((state) => state.misc.colors);

  // Fetch colors from API
  useEffect(() => {
    const fetchColors = async () => {
      if (colors.length === 0) {
        try {
          const response = await getColors();
          dispatch(setColors(response.data || DEFAULT_COLOR_OPTIONS));
        } catch (error) {
          console.error("Error fetching colors:", error);
          dispatch(setError("Failed to fetch colors"));
          // Use default colors as fallback
          dispatch(setColors(DEFAULT_COLOR_OPTIONS));
        }
      }
    };

    fetchColors();
  }, [colors.length, dispatch]);

  const isSelected = (colorOption) => {
    return (
      chosenColor?.color === colorOption.color &&
      chosenColor?.bgcolor === colorOption.bgcolor
    );
  };

  // Use colors from Redux store, fallback to default if empty
  const colorOptions = colors.length > 0 ? colors : DEFAULT_COLOR_OPTIONS;

  return (
    <div className={`flex flex-wrap gap-2 ${className}`}>
      {colorOptions.map((colorOption, index) => (
        <div
          key={`${colorOption.bgcolor}-${colorOption.color}-${index}`}
          onClick={() => !disabled && handleColorChange && handleColorChange(colorOption)}
          className={`
            w-8 h-8 rounded-full cursor-pointer transition-all duration-300 ease-in-out
            flex items-center justify-center
            ${disabled ? "cursor-not-allowed opacity-50" : "hover:scale-110"}
          `}
          style={{
            backgroundColor: isSelected(colorOption)
              ? colorOption.color
              : colorOption.bgcolor,
          }}
          title={colorOption.name || `${colorOption.color} on ${colorOption.bgcolor}`}
        >
          <div
            className="w-5 h-5 rounded-full flex items-center justify-center transition-all duration-300"
            style={{
              border: isSelected(colorOption)
                ? "none"
                : `1px solid ${colorOption.color}`,
              backgroundColor: isSelected(colorOption)
                ? "white"
                : "transparent",
            }}
          >
            {isSelected(colorOption) && (
              <IoCheckmark
                color={colorOption.color}
                size={12}
              />
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default ColorPicker;
